import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Dialog,
  DialogContent,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import React from "react";
import { useForm } from "react-hook-form";

import { formatMoney } from "../../formatters";
import { WieldyStatus } from "../../status";
import useDevMode from "../../utils/devmode";
import { enumToStrings, isoToDate } from "../../utils/utils";
import DataTable, { type DataTableColDef } from "../DataTable";
import Loading from "../Loading";
import { renderCellRedacted } from "../redact";
import CustomNoRowsOverlay from "../TableNoDataOverlay";
import AddEntityActions from "./AddEntityActions";
import {
  defaultProcedureFields,
  editableProcedureFields,
  type ProcedureFormFields,
  procedureFormSchemaBase,
} from "./CrudForms.zod";
import ProcedureForm from "./ProcedureForm";

type ProcedureRow = ProcedureMessage & { key: number };

const columns: DataTableColDef<ProcedureRow>[] = [
  {
    flex: 0.7,
    field: "procedureCode",
    headerName: "Code",
  },
  {
    flex: 0.7,
    field: "procedurePayerSubstitutedCode",
    headerName: "Substituted Code",
  },
  {
    flex: 0.7,
    field: "procedureToothNumber",
    headerName: "Tooth Number",
  },
  {
    flex: 1,
    type: "date",
    field: "procedureDate",
    headerName: "Date of Service",
    valueGetter: (_value, row) =>
      row.procedureDate ? isoToDate(row.procedureDate) : null,
    renderCell: renderCellRedacted,
  },
  {
    flex: 0.7,
    type: "number",
    field: "procedureSubmittedAmount",
    headerName: "Submitted Amount",
    valueFormatter: (value) => formatMoney(value as number | null),
    renderCell: renderCellRedacted,
  },
  {
    flex: 1,
    type: "number",
    field: "procedureContractedAmount",
    headerName: "Contracted Amount",
    valueFormatter: (value) => formatMoney(value as number | null),
    renderCell: renderCellRedacted,
  },
  {
    flex: 1,
    field: "procedureDenialCode",
    headerName: "Denial Reason",
    renderCell: (params) => {
      const iconPath = `/static/images/info-icon-blue.svg`;
      return params.row.procedureDenialDescription ? (
        <Tooltip
          title={
            <div style={{ whiteSpace: "pre-line" }}>
              {params.row.procedureDenialDescription.replaceAll(" | ", "\n\n")}
            </div>
          }
          TransitionComponent={Zoom}
          placement="top"
          arrow
        >
          <div style={{ marginLeft: "16px", marginTop: "4px" }}>
            <img src={iconPath} alt="Details" width="20" height="20" />
          </div>
        </Tooltip>
      ) : (
        <div />
      );
    },
  },
  {
    flex: 1,
    type: "number",
    field: "procedurePayerWriteoff",
    headerName: "Payer Writeoff",
    valueFormatter: (value) => formatMoney(value as number | null),
    renderCell: renderCellRedacted,
  },
  {
    flex: 1,
    type: "number",
    field: "procedurePatientResponsibility",
    headerName: "Patient Pays",
    valueFormatter: (value) => formatMoney(value as number | null),
    renderCell: renderCellRedacted,
  },
  {
    flex: 1,
    type: "number",
    field: "procedurePayerPays",
    headerName: "Payer Pays",
    valueFormatter: (value) => formatMoney(value as number | null),
    renderCell: renderCellRedacted,
  },
];

const getColumns = (
  editProcedure: (procedure: ProcedureMessage) => void,
  deleteProcedure: (procedure: ProcedureMessage) => void,
  devMode: boolean,
): DataTableColDef<ProcedureRow>[] => {
  const allColumns = [...columns];
  if (devMode) {
    const denialReasonIdx = allColumns.findIndex(
      (c) => c.field === "procedureDenialCode",
    );
    allColumns.splice(denialReasonIdx + 1, 0, {
      flex: 1,
      field: "wieldyDenialCategory",
      headerName: "Wieldy Denial Category",
    });
    allColumns.push({
      field: "wieldyStatus",
      headerName: "Wieldy Status",
      type: "singleSelect",
      valueOptions: enumToStrings(WieldyStatus).map((wieldyStatus) => ({
        value: wieldyStatus.toUpperCase(),
        label: wieldyStatus,
      })),
    });
    allColumns.push({
      flex: 1,
      field: "edit",
      headerName: "Edit",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() => {
              editProcedure(params.row);
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              deleteProcedure(params.row);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    });
  }
  return allColumns;
};

interface ProceduresTableProps {
  procedures: ProcedureMessage[];
  isLoading: boolean;
  onSaveProcedure?: (procedure: ProcedureMessage) => Promise<void>;
  onDeleteProcedure?: (procedure: ProcedureMessage) => Promise<void>;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  claim: ClaimWithProcedureAndPatientMessage;
}

export default function ProceduresTable({
  procedures,
  isLoading,
  onSaveProcedure,
  onDeleteProcedure,
  practices,
  supportedSources,
  claim,
}: ProceduresTableProps) {
  const [openProcedureModal, setOpenProcedureModal] = React.useState(false);
  const [procedureToDelete, setProcedureToDelete] =
    React.useState<ProcedureMessage | null>(null);
  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    reset,
  } = useForm<ProcedureFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(procedureFormSchemaBase),
    defaultValues: defaultProcedureFields,
  });

  const devMode = useDevMode();

  const onProcedureModal = (open: boolean) => {
    if (open) {
      reset({
        ...defaultProcedureFields,
        claimKey: claim?.wieldyId || "",
        claimId: claim?.claimId || "",
        practiceId: claim?.practiceId || "",
        source: claim?.source || "",
      });
    } else {
      reset(defaultProcedureFields);
    }
    setOpenProcedureModal(open);
  };

  const onEditProcedure = (procedure: ProcedureMessage) => {
    const formFields = editableProcedureFields.reduce<ProcedureFormFields>(
      (acc, field) =>
        Object.assign(acc, {
          [field.name]:
            procedure[field.name as keyof typeof procedure] ??
            defaultProcedureFields[
              field.name as keyof typeof defaultProcedureFields
            ],
        }),
      { wieldyId: procedure.wieldyId } as ProcedureFormFields,
    );
    reset(formFields);
    setOpenProcedureModal(true);
  };

  const onOpenDeleteModal = (procedure: ProcedureMessage) => {
    setProcedureToDelete(procedure);
  };

  const onCloseDeleteModal = () => {
    setProcedureToDelete(null);
  };

  const onConfirmDeleteProcedures = async () => {
    if (procedureToDelete && onDeleteProcedure) {
      await onDeleteProcedure(procedureToDelete);
    }
    setProcedureToDelete(null);
  };

  const onProcedureFormSubmission = handleSubmit(async (data) => {
    if (onSaveProcedure) {
      await onSaveProcedure(data as ProcedureMessage);
    }
  });

  if (isLoading) {
    return <Loading />;
  }
  const hasErrors = Object.keys(errors).length > 0;
  const procedureIsDisabled = isSubmitting || hasErrors || !isValid;

  return (
    <>
      <Dialog
        open={!!procedureToDelete}
        onClose={onCloseDeleteModal}
        sx={{
          "& .MuiPaper-root": {
            width: "100%",
            padding: "24px 36px",
            margin: "auto",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ marginBottom: 2, p: 0 }} id="delete-procedure-title">
          Delete Procedure
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onCloseDeleteModal}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0, margin: 0 }}>
          <Typography
            paragraph
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              color: "#475467",
              marginBottom: 0,
            }}
          >
            Are you sure you want to delete the procedure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ borderRadius: "8px" }}
            color="error"
            variant="contained"
            onClick={onConfirmDeleteProcedures}
            data-testid="submitCredentials"
          >
            Delete procedure
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: "flex", gap: 1, padding: "4px 4px 0" }}>
        <AddEntityActions
          addEntityForm={
            <ProcedureForm
              control={control}
              practices={practices}
              supportedSources={supportedSources}
              claim={claim}
            />
          }
          handleSubmit={onProcedureFormSubmission}
          isDisabled={procedureIsDisabled}
          enableAddToolBar
          entityName="Procedure"
          setOpenModal={onProcedureModal}
          openModal={openProcedureModal}
        />
      </Box>
      <DataTable
        clientSide
        disablePagination
        showToolbar={false}
        rowHeight={33}
        headerHeight={40}
        rows={procedures.map((procedure, key) => ({ ...procedure, key }))}
        columns={getColumns(onEditProcedure, onOpenDeleteModal, devMode)}
        getRowId={(row) => `${row.wieldyId}-${row.key}`}
        initialSortModel={[{ field: "procedureDate", sort: "desc" }]}
        noRowsOverlay={<CustomNoRowsOverlay />}
        sx={{
          fontSize: "0.8rem",
          "& .DataTable-columnHeaders": {
            backgroundColor: "#F9FAFB",
            fontSize: "0.9rem",
          },
        }}
      />
    </>
  );
}
